// UI helpers da seção de Certidões do Radar de Fornecedor — extraídos do RadarPanel para
// teste unitário. Mapeamento PURO (sem React) de tipo/situação → rótulo do chip.

import type {DadosPfCertidoes} from '../../types/radarFornecedor'

export type SituacaoCertidaoUi = {emoji: string; rotulo: string; cor: string; nota?: string}

// Nome amigável de cada certidão/cadastro retornado pelo backend. A ESTADUAL é montada
// dinamicamente com a UF da sede ("CND Estadual (SEFAZ-UF)") na SecaoCertidoes.
export const CERTIDAO_NOME: Record<string, string> = {
    FEDERAL_RFB_PGFN: 'CND Federal (RFB/PGFN)',
    FGTS_CRF: 'Regularidade FGTS (CRF)',
    TRABALHISTA_CNDT: 'Débitos Trabalhistas (CNDT)',
    ESTADUAL: 'CND Estadual (SEFAZ)',
    INSCRICAO_ESTADUAL_SINTEGRA: 'Comprovante de Inscrição Estadual (SINTEGRA)',
    MUNICIPAL: 'Certidão Municipal',
    INSCRICAO_MUNICIPAL: 'Comprovante de Inscrição Municipal',
    CGU_CORRECIONAL: 'Correcional CGU (ePAD, CGU-PJ, CEIS, CNEP, CEPIM)',
    MPT_FEITOS: 'Feitos — Ministério Público do Trabalho',
    MPF: 'Negativa — Ministério Público Federal',
    TCU_CONSOLIDADA: 'Consulta Consolidada TCU',
    // Conjunto próprio da consulta de CPF (emissão PF destravada 27/08/2026).
    CPF_SITUACAO: 'Situação cadastral do CPF (Receita Federal)',
    CNJ_IMPROBIDADE: 'Improbidade administrativa (CNIA/CNJ)',
    ANTECEDENTES_CRIMINAIS: 'Antecedentes criminais (Polícia Federal)',
    MANDADOS_PRISAO: 'Mandados de prisão (BNMP/CNJ)',
    TSE_QUITACAO: 'Quitação eleitoral (TSE)',
    JUSTICA_ESTADUAL: 'Certidões judiciais estaduais (TJ do domicílio)',
}

// ------------------- Certidões de PESSOA FÍSICA (backend 27/08/2026) -------------------
// A emissão sob demanda foi destravada para CPF com um conjunto próprio de tipos. Só
// orientação (nunca checkbox): JUSTICA_ESTADUAL (SEM link — cada TJ tem o seu portal) e
// TSE_QUITACAO — temporário: rota "Restrita" na Infosimples, conta aguardando
// habilitação; fichas novas nem trazem a linha, e nas antigas ela vira chip manual
// (emitir daria 400 no backend). P/ religar: remover TSE daqui + devolvê-lo a
// TIPOS_CERTIDAO_PF no backend (certidoes.py).
export const TIPOS_SO_ORIENTACAO = ['JUSTICA_ESTADUAL', 'TSE_QUITACAO'] as const

export function ehSoOrientacao(tipo: string, ehCpf = false): boolean {
    // TCU na ficha CPF: a API pública da Consolidada é CNPJ-only (sondagem real de
    // 27/08 — 11 dígitos → 412). A linha PF vira orientação com o link do portal.
    if (ehCpf && tipo === 'TCU_CONSOLIDADA') return true
    return (TIPOS_SO_ORIENTACAO as readonly string[]).includes(tipo)
}

// Consultas SENSÍVEIS/INFORMATIVAS (LGPD): não integram a habilitação e não alteram o
// veredito/parecer — a linha ganha o chip "Informativo" + a microcopy abaixo (o aviso
// formal do backend continua chegando no campo `detalhe`, exibido como nas demais).
export const TIPOS_SENSIVEIS = ['ANTECEDENTES_CRIMINAIS', 'MANDADOS_PRISAO'] as const

export function ehSensivel(tipo: string): boolean {
    return (TIPOS_SENSIVEIS as readonly string[]).includes(tipo)
}

export const MICROCOPY_SENSIVEL =
    'Dado sensível — não integra a habilitação e não altera o parecer; uso restrito à finalidade da contratação (LGPD).'

// -------------- Mini-form de dados do titular (`dados_pf` do POST /certidoes) ----------
// Cada certidão de CPF exige/aproveita campos diferentes do titular. O form da ficha
// mostra SÓ os campos dos tipos marcados e o "Emitir" trava enquanto houver exigência
// vazia. Tabela declarativa: `exigidoPor` bloqueia o botão; `aproveitadoPor` apenas faz
// o campo aparecer (opcional — o órgão usa se vier).
const CAMPOS_PF: {
    campo: keyof DadosPfCertidoes
    exigidoPor: string[]
    aproveitadoPor: string[]
}[] = [
    // A Receita só localiza a situação do CPF com a data de nascimento; antecedentes e a
    // CND Federal por CPF também a EXIGEM (sonda real 27/08: 606 sem birthdate). O TSE
    // apenas aproveita como refino.
    {
        campo: 'data_nascimento',
        exigidoPor: ['CPF_SITUACAO', 'ANTECEDENTES_CRIMINAIS', 'FEDERAL_RFB_PGFN'],
        aproveitadoPor: ['TSE_QUITACAO'],
    },
    // Antecedentes exigem a identificação completa; mandados e TSE só aproveitam (ajudam
    // a desambiguar homônimos no BNMP / a localizar o eleitor).
    {campo: 'nome', exigidoPor: ['ANTECEDENTES_CRIMINAIS'], aproveitadoPor: ['MANDADOS_PRISAO', 'TSE_QUITACAO']},
    {campo: 'nome_mae', exigidoPor: ['ANTECEDENTES_CRIMINAIS'], aproveitadoPor: ['MANDADOS_PRISAO', 'TSE_QUITACAO']},
    {campo: 'nome_pai', exigidoPor: [], aproveitadoPor: ['ANTECEDENTES_CRIMINAIS', 'TSE_QUITACAO']},
    {campo: 'uf_nascimento', exigidoPor: [], aproveitadoPor: ['ANTECEDENTES_CRIMINAIS']},
    // A certidão de feitos do MPT é emitida por procuradoria regional → UF de domicílio.
    {campo: 'uf', exigidoPor: ['MPT_FEITOS'], aproveitadoPor: []},
]

export type CampoPf = {campo: keyof DadosPfCertidoes; exigido: boolean}

// Campos visíveis do mini-form dado o conjunto marcado (ordem estável da tabela).
export function camposPf(selecionadas: Set<string>): CampoPf[] {
    return CAMPOS_PF.filter(
        (c) =>
            c.exigidoPor.some((t) => selecionadas.has(t)) ||
            c.aproveitadoPor.some((t) => selecionadas.has(t))
    ).map((c) => ({campo: c.campo, exigido: c.exigidoPor.some((t) => selecionadas.has(t))}))
}

// Exigências ainda vazias → travam o "Emitir" (o helper text aponta o que falta).
export function faltamDadosPf(
    selecionadas: Set<string>,
    dados: DadosPfCertidoes
): (keyof DadosPfCertidoes)[] {
    return camposPf(selecionadas)
        .filter((c) => c.exigido && !(dados[c.campo] || '').trim())
        .map((c) => c.campo)
}

// Payload `dados_pf`: só os campos VISÍVEIS (dos tipos marcados) e PREENCHIDOS (trim).
// undefined quando não sobra nada — o POST segue sem o bloco (mesmo body do CNPJ).
export function montarDadosPf(
    selecionadas: Set<string>,
    dados: DadosPfCertidoes
): DadosPfCertidoes | undefined {
    const out: DadosPfCertidoes = {}
    for (const {campo} of camposPf(selecionadas)) {
        const v = (dados[campo] || '').trim()
        if (v) out[campo] = v
    }
    return Object.keys(out).length ? out : undefined
}

// Helper text do campo: QUEM o exige (nomes curtos) ou "Opcional".
const EXIGENCIA_CURTA: Record<string, string> = {
    CPF_SITUACAO: 'situação do CPF',
    ANTECEDENTES_CRIMINAIS: 'antecedentes criminais',
    FEDERAL_RFB_PGFN: 'CND Federal',
    MPT_FEITOS: 'certidão do MPT',
}

export function motivoCampoPf(
    campo: keyof DadosPfCertidoes,
    selecionadas: Set<string>
): string {
    const def = CAMPOS_PF.find((c) => c.campo === campo)
    const exigentes = (def?.exigidoPor || [])
        .filter((t) => selecionadas.has(t))
        .map((t) => EXIGENCIA_CURTA[t] || t)
    return exigentes.length ? `Obrigatório para ${exigentes.join(' e ')}` : 'Opcional'
}

// ------------------- Comprovante (recibo) do provedor: validade de 7 dias -------------------
// MEDIDO em 15/10/2026: a URL assinada do recibo da Infosimples expira exatamente 7 dias
// após a emissão (timestamp embutido = emissão+7d; link de 27/08 responde 404). A certidão
// em si vale meses — então o link morto NÃO é certidão vencida. Linhas novas trazem
// `pdf_s3_key` (cópia nossa, permanente); nas antigas aplicamos esta régua ao link original.
export const RECIBO_PROVEDOR_DIAS = 7

export function reciboExpirado(
    c: {link_pdf?: string | null; pdf_s3_key?: string | null; emitida_em?: string | null},
    hoje: string
): boolean {
    if (!c.link_pdf || c.pdf_s3_key) return false
    const em = (c.emitida_em || '').slice(0, 10)
    if (!em) return false // sem data de emissão não dá para afiançar — não esconde o link
    const lim = new Date(`${em}T00:00:00Z`)
    lim.setUTCDate(lim.getUTCDate() + RECIBO_PROVEDOR_DIAS)
    return hoje > lim.toISOString().slice(0, 10)
}

// Semáforo da certidão. A positiva-com-efeitos-de-negativa EQUIVALE à negativa
// (CTN, art. 206) → verde com nota explicativa.
export const SITUACAO_CERTIDAO_UI: Record<string, SituacaoCertidaoUi> = {
    NEGATIVA: {emoji: '🟢', rotulo: 'Regular', cor: '#15803d'},
    POSITIVA_EFEITOS_NEGATIVA: {
        emoji: '🟢',
        rotulo: 'Regular',
        cor: '#15803d',
        nota: 'positiva com efeitos de negativa',
    },
    POSITIVA: {emoji: '🟡', rotulo: 'Constam débitos', cor: '#b45309'},
    INDISPONIVEL: {emoji: '⚪', rotulo: 'Não verificada', cor: '#64748b'},
    // Sob demanda (produto 24/07/2026): certidão ainda não emitida — chip neutro.
    NAO_SOLICITADA: {emoji: '•', rotulo: 'Emitir sob demanda', cor: '#64748b'},
    // SINTEGRA sem inscrição estadual (comum em prestadoras de serviço/software): resultado
    // NEUTRO/informativo — NÃO é falha ("Não verificada") nem regularidade. Chip azul suave.
    SEM_INSCRICAO: {emoji: 'ℹ️', rotulo: 'Sem inscrição estadual', cor: '#2563eb'},
}

// Tipos de CADASTRO (comprovante de inscrição), não CNDs de débito: rótulo próprio quando
// localizados (nunca "Regular"/"Constam débitos") e NUNCA afetam o veredito.
export const INSCRICAO_TIPOS = ['INSCRICAO_ESTADUAL_SINTEGRA', 'INSCRICAO_MUNICIPAL'] as const

export function ehInscricao(tipo: string): boolean {
    return (INSCRICAO_TIPOS as readonly string[]).includes(tipo)
}

// --------- Emissão SELETIVA (checkbox por certidão) — lógica PURA (testável) ----------
// Decisão do dono (27/07): cada emissão é uma chamada PAGA. A ficha nasce com TODAS
// desmarcadas; nada emite até o usuário escolher. "Emitir" manda SÓ as marcadas e fica
// desabilitado enquanto nada estiver selecionado.

// ---------------- Acoplamento imposto pelo BACKEND (CND estadual × SINTEGRA) -------------
// A CND Estadual só é emitida com a Inscrição Estadual em mãos, então o backend adiciona o
// SINTEGRA ao lote SOZINHO quando a ESTADUAL é pedida. A UI espelha isso: o par marca e
// desmarca JUNTO e o SINTEGRA fica TRAVADO enquanto a ESTADUAL estiver marcada — se desse
// para desmarcar, o backend re-adicionaria e o contador do botão mentiria (diria "(1)" e
// sairiam duas emissões). `tipo requisitante` → `tipo pré-requisito`.
export const PRE_REQUISITO: Record<string, string> = {
    ESTADUAL: 'INSCRICAO_ESTADUAL_SINTEGRA',
}

// O tipo + seu pré-requisito, SÓ quando o pré-requisito também está PENDENTE nesta ficha.
// Fora disso o SINTEGRA NÃO vai no payload — mas o backend o adiciona e COBRA mesmo assim
// (vigente ou não; ver `comImplicitos`). Mandar só a ESTADUAL preserva a isenção de cooldown
// do retry de uma ESTADUAL que falhou (o cooldown olha os tipos explícitos).
export function comAcoplados(tipo: string, pendentes: string[]): string[] {
    const pre = PRE_REQUISITO[tipo]
    return pre && pre !== tipo && pendentes.includes(pre) ? [tipo, pre] : [tipo]
}

// O que a UI MOSTRA e CONTA: a seleção + os pré-requisitos que o backend inclui sozinho
// (SINTEGRA sob a ESTADUAL, quando a linha existe), mesmo fora do payload — o contador do
// botão diz o que será cobrado.
export function comImplicitos(selecionadas: Set<string>, elegiveis: string[]): Set<string> {
    const r = new Set(selecionadas)
    selecionadas.forEach((t) => {
        const pre = PRE_REQUISITO[t]
        if (pre && pre !== t && elegiveis.includes(pre)) r.add(pre)
    })
    return r
}

// O checkbox de um pré-requisito fica TRAVADO enquanto quem o exige estiver marcado.
export function selecaoTravada(tipo: string, selecionadas: Set<string>): boolean {
    return Object.keys(PRE_REQUISITO).some(
        (req) => PRE_REQUISITO[req] === tipo && req !== tipo && selecionadas.has(req)
    )
}

// Marca/desmarca UM tipo (novo Set — imutável, bom p/ React), CIENTE do acoplamento:
// marcar/desmarcar a ESTADUAL leva o SINTEGRA com ela; o SINTEGRA travado não alterna
// (devolve o MESMO Set — nada muda, nem re-render).
export function alternarSelecaoAcoplada(
    selecionadas: Set<string>,
    tipo: string,
    pendentes: string[],
): Set<string> {
    if (selecaoTravada(tipo, selecionadas)) return selecionadas
    const grupo = comAcoplados(tipo, pendentes)
    const desmarcando = selecionadas.has(tipo)
    const proximo = new Set(selecionadas)
    for (const t of grupo) {
        if (desmarcando) proximo.delete(t)
        else proximo.add(t)
    }
    return proximo
}

// "Checar todas": se todas já estão marcadas → limpa; senão → marca todas, SEM perder o que o
// usuário já marcou à mão (ex.: uma vigente que ele quer reemitir).
export function alternarTodas(tipos: string[], selecionadas: Set<string>): Set<string> {
    // Sem pendência (tipos vazio) o checkbox desmarcado só pode limpar.
    return !tipos.length || todasSelecionadas(tipos, selecionadas)
        ? new Set()
        : new Set(Array.from(selecionadas).concat(tipos))
}

// Estado do checkbox "Checar todas" (marcado só quando TODAS as linhas estão marcadas).
export function todasSelecionadas(tipos: string[], selecionadas: Set<string>): boolean {
    return tipos.length > 0 && tipos.every((t) => selecionadas.has(t))
}

// Botão "Emitir" só habilita com ao menos uma certidão marcada (não emitir por engano).
export function podeEmitir(qtdSelecionada: number): boolean {
    return qtdSelecionada > 0
}

// Rótulo do botão indicando quantas foram selecionadas (ex.: "Emitir certidões (3)").
export function rotuloEmitir(qtdSelecionada: number): string {
    return qtdSelecionada > 0 ? `Emitir certidões (${qtdSelecionada})` : 'Emitir certidões'
}

// Municipal (CND) SEM rota automática (informativa): o backend devolve INDISPONIVEL porque
// não há como emitir sozinho, não porque falhou. Chip neutro "Manual" e nada a retentar.
function ehMunicipalManual(c: {tipo: string; situacao: string}): boolean {
    return c.tipo === 'MUNICIPAL' && c.situacao === 'INDISPONIVEL'
}

// INDISPONIVEL é FALHA DE COLETA (o órgão não respondeu / caiu no meio), não um resultado:
// o usuário ficou sem documento nenhum. Logo a linha é RETENTÁVEL — nova tentativa, não
// reemissão de documento válido. Só a municipal informativa fica fora (não há rota
// automática para tentar de novo).
export function ehRetentavel(c: {tipo: string; situacao: string}): boolean {
    return c.situacao === 'INDISPONIVEL' && !ehMunicipalManual(c)
}

// Estado do chip de uma linha de certidão/cadastro. `hoje` = 'YYYY-MM-DD' (comparação de
// vigência, mesmo padrão das sanções). Devolve também `manual`/`vencida`/`retry` p/ o render.
export function uiCertidao(
    c: {tipo: string; situacao: string; validade?: string | null},
    hoje: string,
    ehCpf = false,
): SituacaoCertidaoUi & {manual: boolean; vencida: boolean; retry: boolean} {
    // Linhas SÓ-orientação da ficha CPF (TSE / Justiça Estadual): não há rota de emissão
    // pelo app — chip neutro "Emissão manual" (o link/`detalhe` da linha faz o trabalho),
    // nunca "Emitir sob demanda" nem "tentar novamente".
    if (ehSoOrientacao(c.tipo, ehCpf))
        return {emoji: '•', rotulo: 'Emissão manual', cor: '#64748b', manual: true, vencida: false, retry: false}
    const municipalManual = ehMunicipalManual(c)
    const vencida = !municipalManual && !!c.validade && c.validade.slice(0, 10) < hoje
    if (municipalManual)
        return {emoji: '•', rotulo: 'Manual', cor: '#64748b', manual: true, vencida: false, retry: false}
    // Vencida sobrepõe a situação: não há regularidade atual.
    if (vencida)
        return {emoji: '⚪', rotulo: 'Vencida — reemitir', cor: '#64748b', manual: false, vencida: true, retry: false}
    // Falha de coleta → rótulo próprio de RETENTATIVA (mesmo padrão de "Vencida — reemitir"),
    // para não parecer uma emissão nova nem um resultado do órgão.
    if (ehRetentavel(c))
        return {
            emoji: '⚪',
            rotulo: 'Não verificada — tentar novamente',
            cor: '#64748b',
            manual: false,
            vencida: false,
            retry: true,
        }
    // Cadastro (inscrição) localizado → rótulo próprio, verde (não "Regular" de débito).
    if (ehInscricao(c.tipo) && c.situacao === 'NEGATIVA')
        return {emoji: '🟢', rotulo: 'Inscrição localizada', cor: '#15803d', manual: false, vencida: false, retry: false}
    const base = SITUACAO_CERTIDAO_UI[c.situacao] || SITUACAO_CERTIDAO_UI.INDISPONIVEL
    return {...base, manual: false, vencida: false, retry: false}
}

// Guarda de crédito (dono 27/07): "se já foi emitida não deveria dar a opção para emitir de
// novo". Uma linha é EMITÍVEL (mostra checkbox e entra no lote PAGO) SÓ quando não há
// emissão vigente aproveitável: ainda NÃO solicitada, vencida (precisa reemitir) ou
// INDISPONIVEL — falha de coleta, o usuário ficou sem documento e precisa poder RETENTAR
// (sem isso a linha ficava morta e a única saída era refazer a consulta inteira). Ficam de
// FORA — sem checkbox — as já emitidas e vigentes (Regular/Constam débitos com validade no
// futuro), a municipal manual (sem rota automática) e a SEM_INSCRICAO (cadastro inexistente,
// não há o que emitir). Essas seguem só com "Ver PDF" / link externo (que não gastam nosso
// crédito). Reusa a vigência que `uiCertidao` já calcula.
export function ehEmitivel(
    c: {tipo: string; situacao: string; validade?: string | null},
    hoje: string,
    ehCpf = false,
): boolean {
    // Só-orientação (TSE/Justiça na ficha CPF; TCU na CPF) e municipal sem rota:
    // nunca entram no lote pago.
    if (ehSoOrientacao(c.tipo, ehCpf)) return false
    if (ehMunicipalManual(c)) return false
    // Todo o resto pode (RE)EMITIR — inclusive certidão VIGENTE: o recibo do provedor
    // expira em 7 dias (medido 15/10/2026) e o gestor precisa poder gerar outro PDF.
    // O custo segue protegido: seleção explícita por checkbox + cooldown no servidor.
    return true
}

// Pendências de verdade — é o que o "Checar todas" marca (nunca re-emitir tudo por
// engano): não solicitadas, retentáveis, vencidas e as com comprovante expirado.
export function ehPendente(
    c: {
        tipo: string
        situacao: string
        validade?: string | null
        link_pdf?: string | null
        pdf_s3_key?: string | null
        emitida_em?: string | null
    },
    hoje: string,
    ehCpf = false,
): boolean {
    if (ehSoOrientacao(c.tipo, ehCpf) || ehMunicipalManual(c)) return false
    if (c.situacao === 'NAO_SOLICITADA') return true
    if (ehRetentavel(c)) return true
    if (uiCertidao(c, hoje, ehCpf).vencida) return true
    return reciboExpirado(c, hoje)
}
